import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetTaskCategoryByIdQuery,
  useLazyGetTaskCategoriesQuery,
  useCreateTaskCategoryMutation,
  useUpdateTaskCategoryMutation,
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildTaskCategoryRelationParams } from './taskCategoryApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('name', values.name ?? '');
  fd.append('description', values.description ?? '');
  if (values.parent_id) {
    fd.append('parent_id', String(values.parent_id));
  }
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateTaskCategory = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatusesList] = useLazyGetStatusesQuery();
  const [fetchTaskCategories] = useLazyGetTaskCategoriesQuery();

  const taskCategoryStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel('TaskCategory').unwrap();
        } catch {
          return await fetchStatusesList({ paginate: 0 }).unwrap();
        }
      },
    }),
    [fetchStatusesByModel, fetchStatusesList]
  );

  const statusSelect = useLazySelectOptions(taskCategoryStatusTrigger, {
    mapResult: (res) => {
      const list = extractList(res);
      const arr = Array.isArray(list) ? list : [];
      return arr.map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const parentSelect = useLazySelectOptions(fetchTaskCategories, {
    // Options show the category name only — no relations needed
    args: { paginate: 0 },
    mapResult: (res) => {
      const rawList =
        res?.data?.list ?? res?.list ?? extractList(res);
      const arr = Array.isArray(rawList) ? rawList : [];
      const excludeId = isEdit ? editId : null;
      return arr
        .filter((c) => c?.id && String(c.id) !== String(excludeId))
        .map((c) => ({
          id: c.id,
          label: c.name ?? String(c.id),
          raw: c,
        }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error('Could not load statuses');
      }),
    [statusSelect]
  );

  const loadParentCategories = useCallback(() => parentSelect.load(), [parentSelect]);

  const {
    data: categoryData,
    isLoading: categoryLoading,
    isError: categoryError,
    error: categoryErr,
  } = useGetTaskCategoryByIdQuery(
    { id: editId, params: buildTaskCategoryRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createTaskCategory, { isLoading: creating }] = useCreateTaskCategoryMutation();
  const [updateTaskCategory, { isLoading: updating }] = useUpdateTaskCategoryMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (categoryLoading || !hydrated);

  useEffect(() => {
    if (categoryError) {
      toast.error(categoryErr?.data?.message || 'Failed to load task category');
      navigate('/task-categories');
    }
  }, [categoryError, categoryErr, navigate]);

  useEffect(() => {
    if (!isEdit || !categoryData || hydrated) return;
    const record = categoryData?.data ?? categoryData;
    if (!record) return;

    setName(record.name ?? '');
    setDescription(record.description ?? '');

    const st = record.status && typeof record.status === 'object' ? record.status : null;
    const sid = record.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }

    const par = record.parent && typeof record.parent === 'object' ? record.parent : null;
    const pid = record.parent_id ?? par?.id;
    if (pid) {
      if (par) {
        const opt = { id: pid, label: par.name ?? String(pid), raw: par };
        setSelectedParent(opt);
        parentSelect.setOptions((prev) => (prev.some((o) => o.id === pid) ? prev : [...prev, opt]));
      } else {
        const opt = parentSelect.options.find((o) => o.id === pid);
        setSelectedParent(opt ?? { id: pid, label: String(pid), raw: {} });
      }
    } else {
      setSelectedParent(null);
    }
    setHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, categoryData, statusSelect.options, parentSelect.options, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = {};
    if (!name.trim()) clientErrors.name = 'Name is required';
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      parent_id: selectedParent?.id ?? '',
      name: name.trim(),
      description: description.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateTaskCategory({ id: editId, data: fd }).unwrap();
        toast.success('Task category updated');
      } else {
        await createTaskCategory({ data: fd }).unwrap();
        toast.success('Task category added');
      }
      navigate('/task-categories');
    } catch (err) {
      applyApiError(
        err,
        isEdit ? 'Failed to update task category' : 'Failed to add task category'
      );
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-categories')} sx={{ mb: 2 }}>
          Back to task categories
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task category' : 'Add task category'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  options={parentSelect.options}
                  value={selectedParent}
                  onChange={(_, v) => {
                    setSelectedParent(v);
                    clearError('parent_id');
                  }}
                  onOpen={loadParentCategories}
                  loading={parentSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parent category"
                      placeholder="Optional"
                      fullWidth
                      {...fieldProps('parent_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  required
                  fullWidth
                  {...fieldProps('name')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError('description');
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps('description')}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError('status_id');
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      fullWidth
                      {...fieldProps('status_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Add task category'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/task-categories')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskCategory;
